import React, { useState, useEffect } from 'react';
import { 
  X, Power, Users, Zap, Wallet, BrainCircuit, 
  Activity, ShieldCheck, MapPin, Mail, Edit3, Save, Phone, Palette, Image as ImageIcon,
  Sparkles, TrendingUp, AlertCircle, CheckCircle
} from 'lucide-react';
import { db } from '../firebaseConfig';
import { doc, updateDoc, onSnapshot } from 'firebase/firestore';

const StatCard = ({ icon, label, value, subtext, alert }) => (
  <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between">
    <div className="flex justify-between items-start mb-2 md:mb-3">
      <div className={`p-1.5 md:p-2 rounded-xl ${alert ? 'bg-rose-50 text-rose-600' : 'bg-indigo-50 text-indigo-600'}`}>
        {icon}
      </div>
    </div>
    <div>
      <h4 className="text-lg md:text-xl font-black text-slate-900 tracking-tight">{value}</h4>
      <p className="text-[9px] md:text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">{label}</p>
      {subtext && <p className="text-[8px] md:text-[9px] font-medium text-slate-400 mt-1">{subtext}</p>}
    </div>
  </div>
);

const BusinessProfileDrawer = ({ isOpen, onClose, businessData }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [liveData, setLiveData] = useState(null);

  // AI Knowledge States
  const [isEditingAI, setIsEditingAI] = useState(false);
  const [editForm, setEditForm] = useState({ aiKnowledge: '', aiRules: '' });
  const [isSavingAI, setIsSavingAI] = useState(false);

  // Brand & Visuals States
  const [isEditingBrand, setIsEditingBrand] = useState(false);
  const [brandForm, setBrandForm] = useState({ aiImageStyle: '', customBannerUrl: '', brandColor: '' });
  const [isSavingBrand, setIsSavingBrand] = useState(false);

  // 🔥 THE FIX: Hook ko early return se pehle yahan upar rakh diya hai
  const [isUpdatingAutopilot, setIsUpdatingAutopilot] = useState(false);

  useEffect(() => {
    if (!isOpen || !businessData?.id) {
        setIsEditingAI(false); 
        setIsEditingBrand(false);
        return;
    }
    
    // Initial setup
    setLiveData(businessData);

    // 1. Listen to 'clients' collection (For SaaS info, Branding, AI Rules)
    const unsubClient = onSnapshot(doc(db, "clients", businessData.id), (docSnap) => {
      if (docSnap.exists()) {
        setLiveData(prev => ({ ...prev, ...docSnap.data(), id: docSnap.id }));
      }
    });

    // 2. Listen to 'users' collection (For GMB, Wallet, AI Tokens)
    let unsubUser = () => {};
    // Database mein phone number ya whatsappId as document ID use hota hai
    const userPhoneId = businessData.phone || businessData.whatsappId; 
    
    if (userPhoneId) {
        unsubUser = onSnapshot(doc(db, "users", userPhoneId), (userSnap) => {
            if (userSnap.exists()) {
                const uData = userSnap.data();
                // Dono database ki details ko ek sath merge kar do
                setLiveData(prev => ({ ...prev, ...uData }));
            }
        });
    }

    // Cleanup both listeners when drawer closes
    return () => {
        unsubClient();
        unsubUser();
    };
  }, [isOpen, businessData]);

  if (!isOpen || !liveData) return null;

  const isBotPaused = liveData.botPaused === true;

  // 🔥 SMART DATA MAPPING
  const activePlan = liveData.planType || (liveData.isPremium ? "Premium" : "Trial");
  const walletBalance = liveData.walletBalance || 0;  
  const usedTokens = liveData.tokensUsed || liveData.total_ai_tokens || 0;
  const gmbStatus = liveData.googleConnected || liveData.googleAccessToken || liveData.gmbLocationName ? true : false;

  const usagePercent = Math.min((usedTokens / (liveData.tokenQuota || 10000)) * 100, 100).toFixed(0);
  const isHighUsage = usagePercent > 85;

  const toggleBotStatus = async () => {
    if (isUpdating) return;
    setIsUpdating(true);
    try {
      await updateDoc(doc(db, "clients", liveData.id), { botPaused: !isBotPaused });
    } catch (error) { alert("Failed to update bot status."); }
    setTimeout(() => setIsUpdating(false), 500);
  };

  // ==========================================
  // 🔥 AUTO-PILOT & AUTO-REVIEW HANDLER
  // ==========================================
  const isAutoPilotOn = liveData.autoPilotSEO === true;
  const isAutoReviewOn = liveData.autoReviewEnabled === true;

  const toggleAutoPilot = async () => {
    if (isUpdatingAutopilot) return;
    setIsUpdatingAutopilot(true);
    try {
      const userPhoneId = liveData.phone || liveData.whatsappId;
      await updateDoc(doc(db, "clients", liveData.id), { autoPilotSEO: !isAutoPilotOn });
      if (userPhoneId) {
        await updateDoc(doc(db, "users", userPhoneId), { autoPilotSEO: !isAutoPilotOn });
      }
    } catch (error) { alert("Failed to update Auto-Pilot status."); }
    setTimeout(() => setIsUpdatingAutopilot(false), 500);
  };

  const toggleAutoReview = async () => {
    try {
      const userPhoneId = liveData.phone || liveData.whatsappId;
      await updateDoc(doc(db, "clients", liveData.id), { autoReviewEnabled: !isAutoReviewOn });
      if (userPhoneId) {
        await updateDoc(doc(db, "users", userPhoneId), { autoReviewEnabled: !isAutoReviewOn });
      }
    } catch (error) { alert("Failed to update Auto-Review status."); }
  };

  const handleEditClick = () => {
    setEditForm({ aiKnowledge: liveData.aiKnowledge || liveData.businessInfo || '', aiRules: liveData.aiRules || '' });
    setIsEditingAI(true);
  };

  const handleSaveAI = async () => {
    setIsSavingAI(true);
    try {
        await updateDoc(doc(db, "clients", liveData.id), { aiKnowledge: editForm.aiKnowledge, aiRules: editForm.aiRules, businessInfo: editForm.aiKnowledge });
        setIsEditingAI(false);
    } catch (e) { alert("Failed to update AI config"); }
    setIsSavingAI(false);
  };

  const handleBrandEditClick = () => {
    setBrandForm({
        aiImageStyle: liveData.aiImageStyle || '',
        customBannerUrl: liveData.customBannerUrl || '',
        brandColor: liveData.brandColor || '#008069'
    });
    setIsEditingBrand(true);
  };

  const handleSaveBrand = async () => {
    setIsSavingBrand(true);
    try {
        await updateDoc(doc(db, "clients", liveData.id), {
            aiImageStyle: brandForm.aiImageStyle,
            customBannerUrl: brandForm.customBannerUrl,
            brandColor: brandForm.brandColor
        });
        setIsEditingBrand(false);
    } catch (e) { alert("Failed to update Brand config"); }
    setIsSavingBrand(false);
  };

  const currentKnowledge = liveData.aiKnowledge || liveData.businessInfo;
  const currentRules = liveData.aiRules;

  // AI Weekly Report Destructuring
  const weeklyReport = liveData.aiWeeklyReport || null;

  // ✨ MAGIC AUTO-FILL FUNCTIONS
  const handleAutoSuggestAI = () => {
    const bName = liveData.name || "Our Business";
    setEditForm({
      aiKnowledge: `${bName} is a premium training institute. We offer high-quality courses, 100% practical training, and career assistance.`,
      aiRules: "Always be polite, professional, and helpful. Use emojis like 🚀 and ✅. Keep answers under 3-4 lines. Never argue. Always end by asking if they would like to book a free counseling session."
    });
  };

  const handleAutoSuggestBrand = () => {
    setBrandForm({
      ...brandForm,
      brandColor: "#4F46E5", // Premium Indigo Color
      aiImageStyle: "Photorealistic, 8k resolution, cinematic lighting, premium corporate aesthetic, clean and modern layout, highly detailed, professional photography."
    });
  };

  return (
    <div className="fixed inset-0 z-[300] flex justify-end">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" onClick={onClose}></div>

      <div className="relative w-full sm:max-w-[480px] bg-[#F8FAFC] h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-300 border-l border-slate-200">
        
        {/* --- HEADER --- */}
        <div className="px-5 py-4 md:px-8 md:py-6 bg-white border-b border-slate-200 flex justify-between items-start">
          <div className="flex items-center gap-3 md:gap-4">
            <div className="w-12 h-12 md:w-14 md:h-14 bg-indigo-600 text-white rounded-2xl flex items-center justify-center text-lg md:text-xl font-black shadow-lg shadow-indigo-200 shrink-0">
              {liveData.name ? liveData.name.charAt(0).toUpperCase() : 'B'}
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-black text-slate-900 tracking-tight leading-tight line-clamp-1">
                {liveData.name || "Unknown Business"}
              </h2>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <span className="text-[10px] md:text-xs font-mono font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md">{liveData.whatsappId}</span>
                <span className={`text-[8px] md:text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md border ${liveData.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                  {liveData.status || 'TRIAL'}
                </span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 md:p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600 rounded-full transition-colors shrink-0">
            <X size={20} className="md:w-6 md:h-6" />
          </button>
        </div>

        {/* --- SCROLLABLE BODY --- */}
        <div className="p-4 md:p-8 overflow-y-auto flex-1 space-y-4 md:space-y-6 custom-scrollbar">
          
          {/* MASTER CONTROL */}
          <div className="bg-slate-900 rounded-2xl md:rounded-3xl p-5 md:p-6 relative overflow-hidden shadow-xl border border-slate-800">
            <div className={`absolute -top-10 -right-10 w-40 h-40 blur-3xl rounded-full opacity-40 pointer-events-none transition-colors duration-500 ${isBotPaused ? 'bg-rose-500' : 'bg-emerald-500'}`}></div>
            <div className="flex justify-between items-center md:items-start relative z-10 gap-2">
              <div>
                <h3 className="text-[9px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-1.5">
                  <ShieldCheck size={12} className={isBotPaused ? 'text-amber-500' : 'text-emerald-400'}/> Master Engine
                </h3>
                <h4 className="text-lg md:text-xl font-black text-white">{isBotPaused ? 'AI is Paused' : 'AI is Active'}</h4>
                <p className="text-[10px] md:text-xs text-slate-400 mt-1 font-medium max-w-[150px] md:max-w-[200px] leading-tight">
                  {isBotPaused ? 'Human takeover mode. Bot is sleeping.' : 'Listening to incoming WhatsApp data.'}
                </p>
              </div>
              <button 
                onClick={toggleBotStatus} disabled={isUpdating}
                className={`p-3 md:p-4 rounded-xl md:rounded-2xl transition-all shadow-lg shrink-0 group ${isBotPaused ? 'bg-rose-500/10 text-rose-500 hover:bg-rose-500 hover:text-white border border-rose-500/30' : 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500 hover:text-white border border-emerald-500/30'}`}
              >
                {isUpdating ? <div className="w-6 h-6 md:w-8 md:h-8 border-2 border-current border-t-transparent rounded-full animate-spin"></div> : <Power size={24} className="md:w-8 md:h-8 group-active:scale-90 transition-transform" />}
              </button>
            </div>
          </div>

       {/* QUICK METRICS */}
          <div className="grid grid-cols-2 gap-3 md:gap-4">
            <StatCard icon={<Activity size={16} />} label="Plan" value={activePlan} />
            <StatCard icon={<Wallet size={16} />} label="Wallet" value={`₹${walletBalance}`} alert={walletBalance < 100} />
            <StatCard icon={<Zap size={16} />} label="Quota Used" value={`${usagePercent}%`} subtext={`${usedTokens.toLocaleString()} Tokens`} alert={isHighUsage} />
            <StatCard icon={<Users size={16} />} label="GMB Status" value={gmbStatus ? 'Linked ✅' : 'Pending'} />
          </div>

          {/* 🔥 AUTOMATION TOGGLES CARD */}
          <div className="bg-white rounded-2xl md:rounded-3xl border border-slate-200 p-4 md:p-6 shadow-sm space-y-4">
            <h3 className="text-[9px] md:text-[10px] font-black text-slate-800 uppercase tracking-widest flex items-center gap-1.5">
              <Zap size={14} className="text-amber-500" /> AI Automation Toggles
            </h3>
            
            {/* True Auto-Pilot Switch */}
            <div className="flex justify-between items-center bg-slate-50 p-3 rounded-xl border border-slate-100">
              <div>
                <p className="text-xs font-bold text-slate-800">True Auto-Pilot (Weekly SEO)</p>
                <p className="text-[10px] text-slate-500">AI automatically posts every Monday @ 10 AM</p>
              </div>
              <button 
                onClick={toggleAutoPilot}
                className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors duration-300 ${isAutoPilotOn ? 'bg-emerald-500' : 'bg-slate-300'}`}
              >
                <div className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-300 ${isAutoPilotOn ? 'translate-x-6' : 'translate-x-0'}`}></div>
              </button>
            </div>

            {/* Auto-Review Switch */}
            <div className="flex justify-between items-center bg-slate-50 p-3 rounded-xl border border-slate-100">
              <div>
                <p className="text-xs font-bold text-slate-800">Smart Auto-Review Replies</p>
                <p className="text-[10px] text-slate-500">Auto-reply to 4 & 5-star reviews instantly</p>
              </div>
              <button 
                onClick={toggleAutoReview}
                className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors duration-300 ${isAutoReviewOn ? 'bg-emerald-500' : 'bg-slate-300'}`}
              >
                <div className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-300 ${isAutoReviewOn ? 'translate-x-6' : 'translate-x-0'}`}></div>
              </button>
            </div>
          </div>

          {/* 🔮 NEW PRESET: AI WEEKLY STRATEGY RADAR CARD */}
          <div className="bg-gradient-to-br from-indigo-950 via-slate-900 to-indigo-950 rounded-2xl md:rounded-3xl p-5 md:p-6 shadow-xl border border-indigo-500/20 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-48 h-48 bg-indigo-500/10 blur-[60px] rounded-full pointer-events-none"></div>
            
            <div className="relative z-10">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-[9px] md:text-[10px] font-black text-indigo-300 uppercase tracking-widest flex items-center gap-1.5 md:gap-2">
                  <Sparkles size={13} className="text-amber-400 animate-pulse" /> AI Growth Radar
                </h3>
                <span className="text-[9px] md:text-[10px] font-black text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-0.5 rounded-md flex items-center gap-1 uppercase tracking-wider">
                  <TrendingUp size={11} /> {weeklyReport?.score || liveData.seoScore || 75}/100 Health
                </span>
              </div>

              {weeklyReport ? (
                <div className="space-y-3.5">
                  {/* Executive Insight */}
                  <div className="bg-white/5 backdrop-blur-sm border border-white/5 rounded-xl p-3 md:p-4">
                    <h4 className="text-[8px] md:text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-1">
                      <AlertCircle size={11} className="text-amber-400" /> Executive Insight
                    </h4>
                    <p className="text-[11px] md:text-xs font-medium text-slate-200 leading-relaxed">
                      "{weeklyReport.insight}"
                    </p>
                  </div>

                  {/* Recommended Action */}
                  <div className="bg-indigo-500/10 backdrop-blur-sm border border-indigo-500/20 rounded-xl p-3 md:p-4">
                    <h4 className="text-[8px] md:text-[9px] font-bold text-indigo-300 uppercase tracking-widest mb-1 flex items-center gap-1">
                      <CheckCircle size={11} className="text-emerald-400" /> Strategic Action
                    </h4>
                    <p className="text-[11px] md:text-xs font-bold text-white leading-relaxed">
                      👉 {weeklyReport.action}
                    </p>
                  </div>
                </div>
              ) : (
                /* Fallback State if no Weekly Report generated yet */
                <div className="bg-white/5 border border-white/5 rounded-xl p-4 text-center py-6">
                  <BrainCircuit size={22} className="text-indigo-400/70 mx-auto mb-2 animate-bounce" />
                  <h4 className="text-[11px] md:text-xs font-bold text-slate-300 mb-1">Compiling Local Search Architecture...</h4>
                  <p className="text-[9px] md:text-[10px] text-slate-400 max-w-[280px] mx-auto leading-normal">
                    AI is analyzing your competitors, inbound keyword maps, and visibility profiles. Your first premium strategy report goes live on Sunday midnight! ⏳
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* AI KNOWLEDGE INLINE EDITOR */}
          <div className="bg-white rounded-2xl md:rounded-3xl border border-slate-200 p-4 md:p-6 shadow-sm relative overflow-hidden">
             <div className="flex justify-between items-center mb-3 md:mb-4">
              <h3 className="text-[9px] md:text-[10px] font-black text-slate-800 uppercase tracking-widest flex items-center gap-1.5 md:gap-2">
                <BrainCircuit size={14} className="text-indigo-600 md:w-4 md:h-4" /> AI Knowledge
              </h3>
              {!isEditingAI && currentKnowledge && (
                  <button onClick={handleEditClick} className="text-[9px] md:text-[10px] font-bold text-slate-500 hover:text-indigo-600 flex items-center gap-1 transition-colors">
                      <Edit3 size={10} className="md:w-3 md:h-3" /> Edit
                  </button>
              )}
            </div>

           {isEditingAI ? (
                <div className="space-y-3 md:space-y-4 animate-in fade-in duration-300">
                    <div className="flex justify-end">
                        <button onClick={handleAutoSuggestAI} className="text-[9px] md:text-[10px] font-black text-amber-500 bg-amber-50 hover:bg-amber-100 px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors border border-amber-200">
                            <Sparkles size={12} /> Auto-Fill Best Settings
                        </button>
                    </div>
                    <div>
                        <label className="text-[9px] md:text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Business Services & Info</label>
                        <textarea 
                            value={editForm.aiKnowledge} 
                            onChange={(e) => setEditForm({...editForm, aiKnowledge: e.target.value})}
                            className="w-full mt-1 p-2.5 md:p-3 text-[11px] md:text-xs font-medium bg-slate-50 border border-slate-200 rounded-xl focus:border-indigo-500 outline-none resize-none custom-scrollbar h-24 md:h-32"
                            placeholder="Enter business info, pricing, services..."
                        />
                    </div>
                    <div>
                        <label className="text-[9px] md:text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Bot Instructions / Rules</label>
                        <textarea 
                            value={editForm.aiRules} 
                            onChange={(e) => setEditForm({...editForm, aiRules: e.target.value})}
                            className="w-full mt-1 p-2.5 md:p-3 text-[11px] md:text-xs font-medium bg-slate-50 border border-slate-200 rounded-xl focus:border-indigo-500 outline-none resize-none custom-scrollbar h-16 md:h-20"
                            placeholder="e.g. Always be polite, ask for their name..."
                        />
                    </div>
                    <div className="flex justify-end gap-2 pt-1 md:pt-2">
                        <button onClick={() => setIsEditingAI(false)} className="px-3 py-2 md:px-4 md:py-2.5 text-[9px] md:text-[10px] font-black text-slate-500 hover:bg-slate-100 rounded-lg md:rounded-xl uppercase tracking-widest transition-colors">Cancel</button>
                        <button onClick={handleSaveAI} disabled={isSavingAI} className="px-3 py-2 md:px-4 md:py-2.5 text-[9px] md:text-[10px] font-black text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg md:rounded-xl uppercase tracking-widest shadow-md shadow-indigo-200 disabled:opacity-50 flex items-center gap-1.5 md:gap-2 transition-all">
                            {isSavingAI ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : <Save size={12} className="md:w-3 md:h-3"/>}
                            {isSavingAI ? "Saving..." : "Save"}
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    {currentKnowledge ? (
                        <div className="bg-slate-50 p-3 md:p-4 rounded-xl md:rounded-2xl border border-slate-100">
                          <p className="text-[11px] md:text-xs text-slate-600 font-medium whitespace-pre-wrap max-h-32 md:max-h-40 overflow-y-auto custom-scrollbar leading-relaxed">
                            {currentKnowledge}
                          </p>
                        </div>
                    ) : (
                        <div className="bg-amber-50 p-4 md:p-5 rounded-xl md:rounded-2xl border border-amber-100 text-center">
                            <BrainCircuit size={20} className="text-amber-400 mx-auto mb-2 md:w-6 md:h-6" />
                            <p className="text-[10px] md:text-xs font-bold text-amber-800 mb-2 md:mb-3">No AI Knowledge Base!</p>
                            <button onClick={handleEditClick} className="w-full py-2 md:py-2.5 bg-amber-500 text-white text-[9px] md:text-[10px] font-black uppercase tracking-widest rounded-lg md:rounded-xl shadow-sm hover:bg-amber-600 transition-colors">
                                Configure AI Now
                            </button>
                        </div>
                    )}
                    
                    {currentRules && !isEditingAI && (
                       <div className="mt-3 md:mt-5 pt-3 md:pt-4 border-t border-slate-100">
                         <p className="text-[8px] md:text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5 md:mb-2">Bot Rules</p>
                         <p className="text-[11px] md:text-xs text-slate-500 font-medium italic">"{currentRules}"</p>
                       </div>
                    )}
                </>
            )}
          </div>

          {/* BRAND & VISUALS EDITOR */}
          <div className="bg-white rounded-2xl md:rounded-3xl border border-slate-200 p-4 md:p-6 shadow-sm relative overflow-hidden">
            <div className="flex justify-between items-center mb-3 md:mb-4">
              <h3 className="text-[9px] md:text-[10px] font-black text-slate-800 uppercase tracking-widest flex items-center gap-1.5 md:gap-2">
                <Palette size={14} className="text-pink-500 md:w-4 md:h-4" /> Branding & AI Visuals
              </h3>
              {!isEditingBrand && (
                  <button onClick={handleBrandEditClick} className="text-[9px] md:text-[10px] font-bold text-slate-500 hover:text-pink-600 flex items-center gap-1 transition-colors">
                      <Edit3 size={10} className="md:w-3 md:h-3" /> Edit
                  </button>
              )}
            </div>

         {isEditingBrand ? (
                <div className="space-y-3 md:space-y-4 animate-in fade-in duration-300">
                    
                    <div className="flex justify-end">
                        <button onClick={handleAutoSuggestBrand} className="text-[9px] md:text-[10px] font-black text-pink-500 bg-pink-50 hover:bg-pink-100 px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors border border-pink-200">
                            <Sparkles size={12} /> Suggest Premium Style
                        </button>
                    </div>

                    {/* Primary Color Picker */}
                    <div>
                        <label className="text-[9px] md:text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Brand Color (Hex)</label>
                        <div className="flex items-center gap-3 mt-1">
                            <input 
                                type="color" 
                                value={brandForm.brandColor} 
                                onChange={(e) => setBrandForm({...brandForm, brandColor: e.target.value})}
                                className="w-10 h-10 rounded cursor-pointer border-0 bg-transparent" 
                            />
                            <input 
                                type="text"
                                value={brandForm.brandColor}
                                onChange={(e) => setBrandForm({...brandForm, brandColor: e.target.value})}
                                className="w-full p-2.5 md:p-3 text-[11px] md:text-xs font-mono bg-slate-50 border border-slate-200 rounded-xl focus:border-pink-500 outline-none"
                                placeholder="#008069"
                            />
                        </div>
                    </div>

                    {/* Default Banner URL */}
                    <div>
                        <label className="text-[9px] md:text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1"><ImageIcon size={10}/> Default WhatsApp Banner (URL)</label>
                        <input 
                            type="text"
                            value={brandForm.customBannerUrl} 
                            onChange={(e) => setBrandForm({...brandForm, customBannerUrl: e.target.value})}
                            className="w-full mt-1 p-2.5 md:p-3 text-[11px] md:text-xs bg-slate-50 border border-slate-200 rounded-xl focus:border-pink-500 outline-none"
                            placeholder="https://image-url.com/banner.jpg"
                        />
                    </div>

                    {/* AI Image Generation Prompt Addition */}
                    <div>
                        <label className="text-[9px] md:text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">AI Image Style (Prompt Modifier)</label>
                        <textarea 
                            value={brandForm.aiImageStyle} 
                            onChange={(e) => setBrandForm({...brandForm, aiImageStyle: e.target.value})}
                            className="w-full mt-1 p-2.5 md:p-3 text-[11px] md:text-xs bg-slate-50 border border-slate-200 rounded-xl focus:border-pink-500 outline-none resize-none h-20"
                            placeholder="e.g. Photorealistic, 4k, neon lighting, dark mode..."
                        />
                        <p className="text-[8px] md:text-[9px] text-slate-400 mt-1 ml-1 leading-tight">
                            Added to AI image generation prompts to keep visuals on-brand.
                        </p>
                    </div>

                    <div className="flex justify-end gap-2 pt-1 md:pt-2">
                        <button onClick={() => setIsEditingBrand(false)} className="px-3 py-2 md:px-4 md:py-2.5 text-[9px] md:text-[10px] font-black text-slate-500 hover:bg-slate-100 rounded-lg md:rounded-xl uppercase tracking-widest transition-colors">Cancel</button>
                        <button onClick={handleSaveBrand} disabled={isSavingBrand} className="px-3 py-2 md:px-4 md:py-2.5 text-[9px] md:text-[10px] font-black text-white bg-pink-500 hover:bg-pink-600 rounded-lg md:rounded-xl uppercase tracking-widest shadow-md shadow-pink-200 disabled:opacity-50 flex items-center gap-1.5 md:gap-2 transition-all">
                            {isSavingBrand ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : <Save size={12} className="md:w-3 md:h-3"/>}
                            {isSavingBrand ? "Saving..." : "Save"}
                        </button>
                    </div>
                </div>
            ) : (
                <div className="space-y-4">
                    <div className="flex items-center gap-3">
                       <div className="w-8 h-8 rounded-full border shadow-sm" style={{ backgroundColor: liveData.brandColor || '#008069' }}></div>
                       <div>
                          <p className="text-[8px] md:text-[9px] font-black text-slate-400 uppercase tracking-widest">Brand Color</p>
                          <p className="text-[11px] md:text-xs font-mono font-medium text-slate-700">{liveData.brandColor || 'Not set'}</p>
                       </div>
                    </div>
                    {liveData.customBannerUrl && (
                        <div>
                          <p className="text-[8px] md:text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Custom Banner</p>
                          <img src={liveData.customBannerUrl} alt="Banner Preview" className="w-full h-24 object-cover rounded-xl border border-slate-200" />
                        </div>
                    )}
                    {liveData.aiImageStyle && (
                       <div>
                         <p className="text-[8px] md:text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">AI Visual Style</p>
                         <p className="text-[11px] md:text-xs text-slate-600 font-medium italic bg-slate-50 p-2 rounded-lg border border-slate-100">"{liveData.aiImageStyle}"</p>
                       </div>
                    )}
                    {(!liveData.brandColor && !liveData.customBannerUrl && !liveData.aiImageStyle) && (
                        <p className="text-[10px] md:text-xs text-slate-400 italic">No brand settings configured yet.</p>
                    )}
                </div>
            )}
          </div>

          {/* CONTACT INFO */}
          <div className="bg-white rounded-2xl md:rounded-3xl border border-slate-200 p-4 md:p-6 shadow-sm space-y-3 md:space-y-4 mb-4 md:mb-0">
             <div className="flex items-center gap-2 md:gap-3">
                <div className="p-1.5 md:p-2 bg-slate-50 text-slate-400 rounded-lg"><Mail size={12} className="md:w-3.5 md:h-3.5"/></div>
                <div>
                   <p className="text-[8px] md:text-[9px] font-black text-slate-400 uppercase tracking-widest">Registered Email</p>
                   <p className="text-xs md:text-sm font-bold text-slate-700 truncate max-w-[200px]">{liveData.email || liveData.dashboardEmail || "N/A"}</p>
                </div>
             </div>

             <div className="flex items-center gap-2 md:gap-3">
                <div className="p-1.5 md:p-2 bg-emerald-50 text-emerald-500 rounded-lg"><Phone size={12} className="md:w-3.5 md:h-3.5"/></div>
                <div>
                   <p className="text-[8px] md:text-[9px] font-black text-slate-400 uppercase tracking-widest">Lead Alert Number</p>
                   <p className="text-xs md:text-sm font-bold text-slate-700">{liveData.coordinator || "Not Set"}</p>
                </div>
             </div>
             <div className="flex items-center gap-2 md:gap-3">
                <div className="p-1.5 md:p-2 bg-slate-50 text-slate-400 rounded-lg"><MapPin size={12} className="md:w-3.5 md:h-3.5"/></div>
                <div>
                   <p className="text-[8px] md:text-[9px] font-black text-slate-400 uppercase tracking-widest">GMB Location ID</p>
                   <p className="text-[10px] md:text-xs font-mono font-bold text-slate-700 break-all">{liveData.gmbLocationName || "Not Connected"}</p>
                </div>
             </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default BusinessProfileDrawer;